import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as leaderboardApi from "@/api/leaderboard";
import { CompleteEventParams } from "@crypto-trading/common/browser";

/**
 * Query keys for leaderboard-related queries
 */
export const leaderboardKeys = {
  all: ["leaderboard"] as const,
  leaderboard: (limit: number) =>
    [...leaderboardKeys.all, "top", limit] as const,
  stats: (userId: string) => [...leaderboardKeys.all, "stats", userId] as const,
  rank: (userId: string) => [...leaderboardKeys.all, "rank", userId] as const,
  events: () => [...leaderboardKeys.all, "events"] as const,
  userEvents: (userId: string) =>
    [...leaderboardKeys.all, "events", userId] as const,
};

/**
 * Hook to get leaderboard data
 * Refetches every 60 seconds to keep leaderboard relatively fresh
 */
export const useLeaderboard = (limit: number = 100) => {
  return useQuery({
    queryKey: leaderboardKeys.leaderboard(limit),
    queryFn: () => leaderboardApi.getLeaderboard(limit),
    refetchInterval: 60000, // Refetch every 60 seconds
    staleTime: 50000, // Consider data stale after 50 seconds
  });
};

/**
 * Hook to get user's leaderboard stats
 * Refetches every 30 seconds to keep stats up to date
 */
export const useUserStats = (userId: string | undefined) => {
  return useQuery({
    queryKey: leaderboardKeys.stats(userId || ""),
    queryFn: () => leaderboardApi.getUserStats(userId!),
    enabled: !!userId,
  });
};

/**
 * Hook to get user's rank in the leaderboard
 * Refetches every 60 seconds (aligned with leaderboard)
 */
export const useUserRank = (userId: string | undefined) => {
  return useQuery({
    queryKey: leaderboardKeys.rank(userId || ""),
    queryFn: () => leaderboardApi.getUserRank(userId!),
    enabled: !!userId,
    refetchInterval: 60000, // Refetch every 60 seconds
    staleTime: 50000, // Consider data stale after 50 seconds
  });
};

/**
 * Hook to get active events
 * Events rarely change, so refetch less frequently
 */
export const useActiveEvents = () => {
  return useQuery({
    queryKey: leaderboardKeys.events(),
    queryFn: () => leaderboardApi.getActiveEvents(),
    refetchInterval: 300000, // Refetch every 5 minutes
    staleTime: 240000, // Consider data stale after 4 minutes
  });
};

/**
 * Hook to get active events with per-user completion status
 */
export const useUserEvents = (userId: string | undefined) => {
  return useQuery({
    queryKey: leaderboardKeys.userEvents(userId || ""),
    queryFn: () => leaderboardApi.getUserEvents(userId!),
    enabled: !!userId,
    refetchInterval: 300000,
    staleTime: 240000,
  });
};

/**
 * Hook to complete an event
 */
export const useCompleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CompleteEventParams) =>
      leaderboardApi.completeEvent(params),
    onSuccess: (_data, variables) => {
      // Invalidate all relevant queries after successful event completion
      queryClient.invalidateQueries({
        queryKey: leaderboardKeys.stats(variables.userId),
      });
      queryClient.invalidateQueries({
        queryKey: leaderboardKeys.rank(variables.userId),
      });
      // Also invalidate leaderboard as it might have changed
      queryClient.invalidateQueries({
        queryKey: [leaderboardKeys.all[0], "top"],
      });
    },
  });
};
